import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import logo from '../images/logo.png';
import { formatTrainerId, formatName } from '../utils/dataTransformers';
import { 
  FaCalendarAlt, 
  FaClipboardList, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar,
  FaUserCircle,
  FaSignOutAlt,
  FaChevronDown,
  FaCog,
  FaIdBadge,
  FaUsers,
  FaCrown,
  FaUserFriends,
  FaChalkboardTeacher,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import Avatar from './Avatar';
import UserContext from './UserContext';
import Logout from './Logout';

function ConsultantHeader() {
  const navigate = useNavigate()
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const profileRef = useRef(null);
  const serviceDropdownRef = useRef(null);
  const profileTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { session: trainer, setSession} = useContext(UserContext);
  
  // Get services and servicePartnerTypes from trainer session
  const trainerServices = (trainer?.services && trainer.services.length > 0) ? trainer.services : (trainer?.service ? [trainer.service] : []);
  const servicePartnerTypes = trainer?.servicePartnerTypes || {};
  
  // Get active service from localStorage or default to first service
  const [activeService, setActiveService] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('activeService');
      if (saved && trainerServices.includes(saved)) {
        return saved;
      }
    }
    return trainerServices[0] || null;
  });
  
  // Update activeService when trainer services change
  useEffect(() => {
    if (trainerServices.length > 0 && (!activeService || !trainerServices.includes(activeService))) {
      const saved = localStorage.getItem('activeService');
      if (saved && trainerServices.includes(saved)) {
        setActiveService(saved);
      } else {
        setActiveService(trainerServices[0]);
        localStorage.setItem('activeService', trainerServices[0]);
      }
    }
  }, [trainerServices, activeService]);
  
  // Filter to only consultant services
  const consultantServices = trainerServices.filter(service => 
    (servicePartnerTypes[service] || 'instructor') === 'consultant'
  );
  
  // Check if user has instructor services (for showing Trainer Dashboard link)
  const hasInstructorServices = trainerServices.some(service => 
    (servicePartnerTypes[service] || 'instructor') === 'instructor'
  );
  
  // Check if trainer has both types (show toggle)
  const hasBothTypes = hasInstructorServices && consultantServices.length > 0;
  
  // Get current dashboard preference - sync with current route
  const [dashboardPreference, setDashboardPreference] = useState(() => {
    // If on consultant dashboard, preference should be consultant
    if (location.pathname.includes('/trainer/consultant-dashboard')) {
      return 'consultant';
    }
    return localStorage.getItem('dashboardPreference') || 'consultant';
  });
  
  // Update preference when route changes
  useEffect(() => {
    if (location.pathname.includes('/trainer/consultant-dashboard')) {
      setDashboardPreference('consultant');
      localStorage.setItem('dashboardPreference', 'consultant');
    } else if (location.pathname.includes('/trainer/home')) {
      setDashboardPreference('instructor');
      localStorage.setItem('dashboardPreference', 'instructor');
    }
  }, [location.pathname]);
  
  // Check if trainer is on advance plan
  const isAdvancePlan = trainer?.subscriptionPlan === 'advance';
  
  // Handle dashboard toggle
  const handleDashboardToggle = () => {
    const newPreference = dashboardPreference === 'consultant' ? 'instructor' : 'consultant';
    setDashboardPreference(newPreference);
    localStorage.setItem('dashboardPreference', newPreference);
    
    // Navigate to the appropriate dashboard
    if (newPreference === 'instructor') {
      navigate('/trainer/home', { replace: true });
    } else {
      navigate('/trainer/consultant-dashboard', { replace: true });
    }
  };
  
  // Handle service change
  const handleServiceChange = (service) => {
    setActiveService(service);
    localStorage.setItem('activeService', service);
    setIsServiceDropdownOpen(false);
    // Redirect to consultant dashboard when service changes
    if (window.location.pathname.includes('/trainer/classes')) {
      navigate('/trainer/consultant-dashboard');
    }
  };
  
  // Check screen size and set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside or when cursor leaves
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setIsServiceDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(profileTimeoutRef.current);
    };
  }, []);

  const handleProfileInteraction = () => {
    clearTimeout(profileTimeoutRef.current);
    setIsProfileOpen(true);
  };

  const handleProfileLeave = () => {
    profileTimeoutRef.current = setTimeout(() => {
      setIsProfileOpen(false);
    }, 200);
  };

  const handleDropdownEnter = () => {
    clearTimeout(profileTimeoutRef.current);
  };

  const handleDropdownLeave = () => {
    setIsProfileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-purple-800 via-indigo-900 to-purple-900 text-white shadow-2xl animate-fade-in border-b-2 border-purple-400/40">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-2">
        {/* Logo and Brand */}
        <div className="flex items-center">
          <Link to="/trainer/consultant-dashboard" className="relative group/logo">
            <div className="absolute -inset-1 bg-gradient-to-br from-purple-300/40 via-indigo-300/30 to-transparent blur-[12px] rounded-full opacity-70 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
            <div className="relative transform group-hover/logo:scale-[1.02] transition-transform duration-300">
              <img 
                src={logo} 
                alt="Yogvital Logo" 
                className="h-15 w-auto drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]" 
              />
            </div>
          </Link>
          
          <Link to="/trainer/consultant-dashboard" className="ml-3 group/text flex items-center gap-2">
            <h1 className="text-3xl font-bold mt-1 font-display group-hover/text:text-purple-300 transition-colors duration-300 drop-shadow-lg">
              Yogvital
            </h1>
            {isAdvancePlan && (
              <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-lg animate-pulse">
                <FaCrown className="text-xs" />
                <span className="hidden sm:inline">Advance Plan</span>
              </span>
            )}
          </Link>
        </div>

        {/* Desktop Menu - now shows at 769px and above */}
        <div className={`${isMobile ? 'hidden' : 'flex'} items-center space-x-2`}>
          {/* Dashboard Toggle - Only show if trainer has both instructor and consultant services */}
          {hasBothTypes && (
            <div className="relative mr-2">
              <button
                onClick={handleDashboardToggle}
                className="flex items-center px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 border border-purple-300/50 bg-purple-900/30"
                title={`Switch to ${dashboardPreference === 'consultant' ? 'Instructor' : 'Consultant'} Dashboard`}
              >
                {dashboardPreference === 'consultant' ? (
                  <>
                    <FaCalendarAlt className="mr-2 text-white" />
                    <span className="font-medium text-sm mr-2">Consultant</span>
                    <FaToggleOn className="text-white/80" />
                  </>
                ) : (
                  <>
                    <FaChalkboardTeacher className="mr-2 text-white" />
                    <span className="font-medium text-sm mr-2">Instructor</span>
                    <FaToggleOff className="text-white/80" />
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Service Switcher - Only show if multiple consultant services */}
          {consultantServices.length > 1 && (
            <div className="relative mr-2" ref={serviceDropdownRef}>
              <button
                onClick={() => setIsServiceDropdownOpen(!isServiceDropdownOpen)}
                className="flex items-center px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 border border-purple-300/50 bg-purple-900/30"
              >
                <span className="font-medium text-sm mr-2">{activeService || 'Select Service'}</span>
                <FaChevronDown className={`text-white/80 transition-transform ${isServiceDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
              {isServiceDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  {consultantServices.map((service) => (
                    <button
                      key={service}
                      onClick={() => handleServiceChange(service)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        activeService === service ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{service}</span>
                        <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                          Consultant
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Show Trainer Dashboard link if user has instructor services - replaced by toggle if both types */}
          {hasInstructorServices && !hasBothTypes && (
            <Link
              to="/trainer/home"
              className="flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border border-transparent hover:border-cyan-300/50"
            >
              <FaChalkboardTeacher className="mr-2 text-white group-hover:text-cyan-300 transition-colors" />
              <span className="font-medium">Trainer Dashboard</span>
            </Link>
          )}
          
          <Link
            to="/trainer/appointments"
            className={`flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border ${
              location.pathname === '/trainer/appointments'
                ? 'border-purple-300/70 bg-white/10' 
                : 'border-transparent hover:border-purple-300/50'
            }`}
          >
            <FaCalendarAlt className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
            <span className="font-medium">Appointments</span>
          </Link>
          
          <Link
            to="/trainer/consultant-programs"
            className={`flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border ${
              location.pathname === '/trainer/consultant-programs'
                ? 'border-purple-300/70 bg-white/10' 
                : 'border-transparent hover:border-purple-300/50'
            }`}
          >
            <FaClipboardList className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
            <span className="font-medium">Programs</span>
          </Link>
          
          <Link
            to="/trainer/fee-plan"
            className={`flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border ${
              location.pathname === '/trainer/fee-plan'
                ? 'border-purple-300/70 bg-white/10' 
                : 'border-transparent hover:border-purple-300/50'
            }`}
          >
            <FaMoneyBillWave className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
            <span className="font-medium">Pricing</span>
          </Link>
          
          <Link
            to="/trainer/students"
            className={`flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border ${
              location.pathname === '/trainer/students'
                ? 'border-purple-300/70 bg-white/10' 
                : 'border-transparent hover:border-purple-300/50'
            }`}
          >
            <FaUserFriends className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
            <span className="font-medium">My Clients</span>
          </Link>
          
          <Link
            to="/trainer/fees"
            className={`flex items-center px-4 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 group border ${
              location.pathname === '/trainer/fees' 
                ? 'border-purple-300/70 bg-white/10' 
                : 'border-transparent hover:border-purple-300/50'
            }`}
          >
            <FaMoneyBillWave className="mr-2 text-white group-hover:text-purple-300 transition-colors" />
            <span>Fees</span>
          </Link>
          
          {/* Profile Dropdown */}
          <div 
            className="relative ml-2" 
            ref={profileRef}
            onMouseEnter={handleProfileInteraction}
            onMouseLeave={handleProfileLeave}
          >
            <button
              onClick={isMobile ? () => setIsProfileOpen(!isProfileOpen) : undefined}
              className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-white/20 hover:shadow-lg transition-all duration-200 focus:outline-none border border-transparent hover:border-purple-300/50"
            >
              <div className="relative">
                <Avatar
                  src={trainer?.profileImage}
                  name={trainer?.name || 'Consultant'}
                  size={32}
                  className="border-2 border-purple-300/70 shadow-lg"
                  alt="Profile"
                />
                {trainer?.status === 'active' && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              <FaChevronDown className={`text-white/80 transition-transform ${isProfileOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {isProfileOpen && (
              <div 
                className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center mb-2">
                    <FaIdBadge className="text-gray-400 mr-2" />
                    <p className="text-xs text-gray-500">ID: {formatTrainerId(trainer?.trainerId || trainer?.id, trainer?.name) || 'N/A'}</p>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900">{formatName(trainer?.name) || 'Consultant'}</p>
                    {isAdvancePlan && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-md">
                        <FaCrown className="text-[10px]" />
                        <span>Advance</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{trainer?.email || ''}</p>
                </div>
                <Link
                  to="/trainer/trainer-profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <FaUserCircle className="mr-3 text-gray-500" />
                  <span>My Profile</span>
                </Link>
                <Link
                  to="/trainer/settings"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <FaCog className="mr-3 text-gray-500" />
                  <span>Settings</span>
                </Link>
                {/* Dashboard Toggle in Profile Menu */}
                {hasBothTypes && (
                  <button
                    onClick={() => {
                      handleDashboardToggle();
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {dashboardPreference === 'consultant' ? (
                      <>
                        <FaChalkboardTeacher className="mr-3 text-gray-500" />
                        <span>Switch to Instructor Dashboard</span>
                      </>
                    ) : (
                      <>
                        <FaCalendarAlt className="mr-3 text-gray-500" />
                        <span>Switch to Consultant Dashboard</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    Logout(navigate, setSession);
                    setIsProfileOpen(false);
                  }}
                  className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                >
                  <FaSignOutAlt className="mr-3 text-gray-500" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Header Right Section */}
        {isMobile && (
          <div className="flex items-center space-x-4">
            {/* Mobile Profile Dropdown */}
            <div 
              className="relative" 
              ref={profileRef}
              onMouseEnter={handleProfileInteraction}
              onMouseLeave={handleProfileLeave}
            >
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="focus:outline-none"
                aria-label="Profile menu"
              >
                <div className="relative">
                  <Avatar
                    src={trainer?.profileImage}
                    name={trainer?.name || 'Consultant'}
                    size={40}
                    className="border-2 border-purple-300/70 shadow-lg"
                    alt="Profile"
                  />
                  {trainer?.status === 'active' && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  )}
                </div>
              </button>

              {isProfileOpen && (
                <div 
                  className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-900">{formatName(trainer?.name) || 'Consultant'}</p>
                      {isAdvancePlan && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-semibold rounded-full shadow-md">
                          <FaCrown className="text-[10px]" />
                          <span>Advance</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">ID: {formatTrainerId(trainer?.trainerId || trainer?.id, trainer?.name) || 'N/A'}</p>
                  </div>
                  <Link
                    to="/trainer/trainer-profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <FaUserCircle className="mr-3 text-gray-500" />
                    <span>My Profile</span>
                  </Link>
                  <Link
                    to="/trainer/settings"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <FaCog className="mr-3 text-gray-500" />
                    <span>Settings</span>
                  </Link>
                  {/* Dashboard Toggle in Mobile Profile Menu */}
                  {hasBothTypes && (
                    <button
                      onClick={() => {
                        handleDashboardToggle();
                        setIsProfileOpen(false);
                      }}
                      className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {dashboardPreference === 'consultant' ? (
                        <>
                          <FaChalkboardTeacher className="mr-3 text-gray-500" />
                          <span>Switch to Instructor Dashboard</span>
                        </>
                      ) : (
                        <>
                          <FaCalendarAlt className="mr-3 text-gray-500" />
                          <span>Switch to Consultant Dashboard</span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      Logout(navigate, setSession);
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                  >
                    <FaSignOutAlt className="mr-3 text-gray-500" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </nav>
    </header>
  );
}

export default ConsultantHeader;

